import {AirportLocation, ShippingAddress} from '../definitions'

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
const googleGeocodeEndpoint = 'https://maps.googleapis.com/maps/api/geocode/json'

export const convertAddressToString = (address: ShippingAddress): string => {
  const parts = [
    address.street1,
    address.street2,
    address.city,
    address.state,
    address.zip,
    address.country,
  ]

  const filteredParts = parts.filter(Boolean)

  return filteredParts.join(', ')
}

export const getLatLngFromAddress = async (
  address: ShippingAddress
): Promise<google.maps.LatLngLiteral> => {
  const addressString = convertAddressToString(address)
  if (!addressString) return {lat: 0, lng: 0}

  const response = await fetch(
    `${googleGeocodeEndpoint}?address=${encodeURIComponent(addressString)}&key=${API_KEY}`
  )
  const data = await response.json()

  if (data.status === 'OK') {
    return data.results[0].geometry.location
  } else {
    return {lat: 0, lng: 0}
  }
}
export const searchAddress = async (input: string): Promise<any | null> => {
  if (!input?.trim()) return null

  let result = await fetchGeocode({address: input})
  if (!result) return null

  if (!hasPostalCode(result)) {
    const lat = result.geometry?.location?.lat
    const lng = result.geometry?.location?.lng
    const reverseResult = await fetchGeocode({latlng: `${lat},${lng}`})
    if (reverseResult) result = reverseResult
  }
  return result
}

export const fetchGeocode = async (params: Record<string, string>) => {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') searchParams.append(key, value)
  }
  searchParams.append('key', API_KEY)
  const url = `${googleGeocodeEndpoint}?${searchParams.toString()}`
  const res = await fetch(url)
  const json = await res.json()
  if (json.status !== 'OK' || !json.results?.length) return null
  return json.results[0]
}

export const hasPostalCode = (data: any): boolean =>
  Array.isArray(data?.address_components) &&
  data.address_components.some((c: any) =>
    c.types.some((t: string) => t === 'postal_code' || t === 'postal_code_suffix')
  )

/**
 * Get airport location (lat/lng) by IATA code using Google Places API.
 * @param {string} iataCode - The IATA airport code (e.g., 'LAX', 'HAN').
 * @returns {Promise<AirportLocation>}
 */
export const getAirportLocation = async (iataCode: string): Promise<AirportLocation | null> => {
  return new Promise((resolve) => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.error('Google Maps JS SDK not loaded yet')
      return resolve(null)
    }

    const map = new window.google.maps.Map(document.createElement('div')) // invisible map

    const service = new window.google.maps.places.PlacesService(map)

    service.findPlaceFromQuery(
      {
        query: `${iataCode} Airport`,
        fields: ['geometry', 'name'],
      },
      (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results?.length) {
          const airport = results[0]
          resolve({
            lat: airport.geometry?.location?.lat() || 0,
            lng: airport.geometry?.location?.lng() || 0,
            name: airport.name || '',
            code: iataCode,
          })
        } else {
          console.warn(`Could not find airport: ${iataCode}`, status)
          resolve(null)
        }
      }
    )
  })
}
